"use client";

import Link from "next/link";
import { useRef, useState, useCallback } from "react";
import { GridFour as LayoutDashboard, SignOut as LogOut } from "@phosphor-icons/react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogoutForm, LogoutSubmitButton } from "@/components/auth/logout-button";

/** Delay before opening menu on hover; avoids opening on quick pass-through. */
const HOVER_OPEN_DELAY_MS = 120;
/** Delay before closing after pointer leaves; allows time to move to portaled content. */
const HOVER_CLOSE_DELAY_MS = 280;
/** After opening, ignore trigger leave for this period to avoid flicker from portal opening. */
const OPEN_GRACE_MS = 120;

export interface ISiteHeaderAuthUser {
  name: string;
  email: string;
  avatar?: string;
}

/**
 * Derives a single uppercase initial for Avatar fallback from name or email.
 */
function getInitial(name: string, email: string): string {
  const trimmed = name?.trim();
  if (trimmed?.length) return trimmed.charAt(0).toUpperCase();
  const first = email?.trim().charAt(0);
  return first ? first.toUpperCase() : "?";
}

export function SiteHeaderAuth({ user }: { user: ISiteHeaderAuthUser }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedAtRef = useRef<number>(0);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current != null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const clearOpenTimeout = useCallback(() => {
    if (openTimeoutRef.current != null) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const handleTriggerEnter = useCallback(() => {
    clearCloseTimeout();
    clearOpenTimeout();
    openTimeoutRef.current = setTimeout(() => {
      openTimeoutRef.current = null;
      openedAtRef.current = Date.now();
      setOpen(true);
    }, HOVER_OPEN_DELAY_MS);
  }, [clearCloseTimeout, clearOpenTimeout]);

  const handleTriggerLeave = useCallback(() => {
    clearOpenTimeout();
    if (!open) return;
    if (Date.now() - openedAtRef.current < OPEN_GRACE_MS) return;
    scheduleClose();
  }, [open, scheduleClose]);

  const handleContentEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handleContentLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  const initial = getInitial(user.name, user.email);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="User menu"
          onMouseEnter={handleTriggerEnter}
          onMouseLeave={handleTriggerLeave}
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src={user.avatar} alt={user.name} />
            <AvatarFallback className="bg-muted text-muted-foreground text-sm font-medium">
              {initial}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        onMouseEnter={handleContentEnter}
        onMouseLeave={handleContentLeave}
        className="min-w-[10rem]"
      >
        <DropdownMenuItem asChild>
          <Link href="/dashboard" className="flex items-center gap-2 cursor-pointer">
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </Link>
        </DropdownMenuItem>
        <LogoutForm>
          <DropdownMenuItem asChild onSelect={(e) => e.preventDefault()}>
            <LogoutSubmitButton
              className="flex w-full cursor-pointer items-center gap-2"
              icon={<LogOut className="h-4 w-4" />}
            >
              Logout
            </LogoutSubmitButton>
          </DropdownMenuItem>
        </LogoutForm>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
